/**
 * 消息 → 动作按钮映射（附件4 §04「每条消息带动作按钮直接跳办」）
 * -----------------------------------------------------------------------------
 * 为什么映射放在**前端**而不是服务端下发路由：路由是各端的导航结构，
 * 服务端只负责说明「这条消息跟哪张报名单有关」（`InboxMessageView.registrationId`），
 * 由各端自己决定点进去到哪。把 path 下发进契约会把三端导航焊死在接口字段上。
 *
 * 两条不变量：
 *   1. **没有指向单据就没有动作** —— 报名单 id 为空时一律不给按钮。
 *      平台类事件（如 `renewal.reminder`）本就没有指向对象，
 *      臆造一个「去某处」比不给按钮更糟。
 *   2. **动作必须落到具体那一单** —— 同批次可有多单（D-01 允许跨批次复报），
 *      因此路由一律带 `registrationId`，绝不跳到「列表页让学员自己找」。
 *
 * ⚠️ 物流类消息跳的是**报名详情**：一期**不接快递系统**（地址由后台手动导出给快递公司），
 * 因此 H5 不做运单号展示，只在报名详情里渲染「教材」区块（领取方式 + 发放状态）。
 * `/logistics/track` 已降级为纯说明页，不作为消息落点（它拿不到报名单 id）。
 *
 * 事件码清单**冻结为 13 类**（`NotifyEventCode`），下表用 `Record<NotifyEventCode, …>`
 * 声明，因此新增事件码会**编译报错**而不是静默没有按钮。
 */
import { NotifyEventCode } from '@enroll/shared';

export interface InboxAction {
  label: string;
  /** 相对路由（含 `registrationId`） */
  route: string;
}

/** `{id}` 为报名单 id 占位符，取值时替换 */
interface ActionRule {
  label: string;
  path: string;
}

const ACTION_BY_EVENT: Record<NotifyEventCode, ActionRule | null> = {
  // 「已提交 · 进入待缴费」→ 直接去缴费页（该单此时必为 PENDING_PAYMENT）
  [NotifyEventCode.REGISTRATION_SUBMITTED]: { label: '去缴费', path: '/payment/{id}' },
  // 驳回后原单可改可重提，落到该单的编辑页（ID 不变，见状态机）
  [NotifyEventCode.REVIEW_REJECTED]: { label: '修改后重新提交', path: '/enroll/{id}' },
  [NotifyEventCode.PAYMENT_REMINDER]: { label: '去缴费', path: '/payment/{id}' },
  [NotifyEventCode.PAYMENT_RECONCILED]: { label: '查看报名单', path: '/registration/{id}' },
  // 需补寄材料：学员要先核对收件信息，统一落到详情页（地址修改窗口与详情一致）
  [NotifyEventCode.MATERIAL_SUPPLEMENT_REQUIRED]: {
    label: '查看报名单',
    path: '/registration/{id}',
  },
  // 一期不接快递系统，只有发放状态可看 —— 按钮文案跟着口径走，别说「物流」
  [NotifyEventCode.LOGISTICS_SHIPPED]: { label: '查看发放状态', path: '/registration/{id}' },
  [NotifyEventCode.LOGISTICS_ABNORMAL]: { label: '查看发放状态', path: '/registration/{id}' },
  // 缴费满 2 天仍未申请领取的提醒（2026-10-22 新增第 13 类）→ 直接落到该单的领取页，
  // 让学员当场确认收件信息并提交申请；跳「我的报名」等于让他自己再找一遍。
  [NotifyEventCode.MATERIAL_APPLY_REMINDER]: { label: '去领取教材', path: '/logistics/{id}' },
  [NotifyEventCode.COURSE_STARTING]: { label: '查看报名单', path: '/registration/{id}' },
  [NotifyEventCode.EXAM_REMINDER]: { label: '查看报名单', path: '/registration/{id}' },
  [NotifyEventCode.RESULT_PUBLISHED]: { label: '查看成绩', path: '/registration/{id}' },
  [NotifyEventCode.INVOICE_ISSUED]: { label: '查看报名单', path: '/registration/{id}' },
  // 平台类事件：到期续报提醒与任何单无关，刻意**不给按钮**（它没有指向对象）
  [NotifyEventCode.RENEWAL_REMINDER]: null,
};

/**
 * 取某条消息的动作按钮；无动作返回 `null`（调用方不渲染按钮）。
 * 未知事件码按「无动作」处理，不抛错 —— 消息中心是兜底留档，新事件码先落档再升级前端。
 */
export function inboxActionOf(
  eventCode: string,
  registrationId: string | null,
): InboxAction | null {
  const rule = (ACTION_BY_EVENT as Record<string, ActionRule | null | undefined>)[eventCode];
  if (!rule) return null;
  if (!registrationId) return null;
  return {
    label: rule.label,
    route: rule.path.replace('{id}', encodeURIComponent(registrationId)),
  };
}
